import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../../api/client.js';
import type { BotResponseRule, BotSettings, BotVariable } from '../../api/types.js';
import { Alert } from '../../components/Alert.js';
import { EmptyState } from '../../components/EmptyState.js';
import { StatusPill } from '../../components/StatusPill.js';
import { useSession } from '../../auth/session-context.js';
import { VariableHelp } from './BotResponseFields.js';

/**
 * Automatic replies to a phrase (§102, module brief §41).
 *
 * Three literal matchers and no pattern language. Regex is deferred to a later
 * slice on its own merits: a bad pattern here makes the workspace SAY something
 * to its audience on every line of chat, which is a different blast radius from
 * a bad pattern in Moderator's internal queue.
 *
 * §41 also forbids adding NLP or AI here, and nothing does.
 */

const KINDS = [
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'exact', label: 'is exactly' },
] as const;

export function BotResponses({
  settings,
  variables,
}: {
  settings: BotSettings;
  variables: BotVariable[];
}): React.JSX.Element {
  const { can } = useSession();
  const mayManage = can('bot.responses.manage');

  const [rules, setRules] = useState<BotResponseRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{
    id: string | null;
    name: string;
    kind: BotResponseRule['kind'];
    triggerValue: string;
    body: string;
    cooldownSeconds: number;
    enabled: boolean;
  } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await apiRequest<{ rules: BotResponseRule[] }>('/bot/responses');
      setRules(data.rules);
    } catch (caught) {
      setRules([]);
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not read the replies.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (): Promise<void> => {
    if (draft === null) return;
    setBusy(true);
    setError(null);

    try {
      await apiRequest(draft.id === null ? '/bot/responses' : `/bot/responses/${draft.id}`, {
        method: draft.id === null ? 'POST' : 'PUT',
        body: {
          name: draft.name,
          enabled: draft.enabled,
          kind: draft.kind,
          triggerValue: draft.triggerValue,
          body: draft.body,
          cooldownSeconds: draft.cooldownSeconds,
          targetMode: 'origin',
          targetPlatforms: [],
        },
      });
      setDraft(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That reply could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bot__section">
      {error !== null && <Alert tone="error">{error}</Alert>}

      {mayManage && draft === null && (
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setDraft({
              id: null,
              name: '',
              kind: 'contains',
              triggerValue: '',
              body: '',
              cooldownSeconds: 60,
              enabled: true,
            });
          }}
        >
          New automatic reply
        </button>
      )}

      {draft !== null && (
        <form
          className="bot__form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="composer__field">
            <label className="field__label" htmlFor="rule-name">
              Name
            </label>
            <input
              id="rule-name"
              className="field__input"
              value={draft.name}
              onChange={(event) => {
                setDraft({ ...draft, name: event.target.value });
              }}
            />
          </div>

          <div className="bot__row">
            <div className="composer__field">
              <label className="field__label" htmlFor="rule-kind">
                When a message
              </label>
              <select
                id="rule-kind"
                className="field__input"
                value={draft.kind}
                onChange={(event) => {
                  setDraft({ ...draft, kind: event.target.value as BotResponseRule['kind'] });
                }}
              >
                {KINDS.map((kind) => (
                  <option key={kind.value} value={kind.value}>
                    {kind.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="composer__field">
              <label className="field__label" htmlFor="rule-trigger">
                This phrase
              </label>
              <input
                id="rule-trigger"
                className="field__input"
                value={draft.triggerValue}
                placeholder="discord"
                onChange={(event) => {
                  setDraft({ ...draft, triggerValue: event.target.value });
                }}
              />
              <span className="field__hint">
                Plain text, not a pattern. Case and extra spaces are ignored.
              </span>
            </div>

            <div className="composer__field">
              <label className="field__label" htmlFor="rule-cooldown">
                Cooldown (seconds)
              </label>
              <input
                id="rule-cooldown"
                className="field__input"
                type="number"
                min={0}
                value={draft.cooldownSeconds}
                onChange={(event) => {
                  setDraft({ ...draft, cooldownSeconds: Number(event.target.value) });
                }}
              />
            </div>
          </div>

          <div className="composer__field">
            <label className="field__label" htmlFor="rule-body">
              Reply with
            </label>
            <textarea
              id="rule-body"
              className="field__input"
              rows={2}
              value={draft.body}
              onChange={(event) => {
                setDraft({ ...draft, body: event.target.value });
              }}
            />
          </div>

          {/*
           * The loop warning, where an operator is about to cause one. A reply
           * containing its own trigger word is the classic mistake, and
           * Connaro's loop prevention catches it — but knowing beforehand is
           * better than a suppressed reply nobody can explain.
           */}
          {draft.triggerValue.trim() !== '' &&
            draft.body.toLowerCase().includes(draft.triggerValue.trim().toLowerCase()) && (
              <Alert tone="warning">
                This reply contains its own trigger phrase. Connaro will not answer its own
                messages, but on platforms where it cannot recognise them the reply may be
                suppressed — consider wording it differently.
              </Alert>
            )}

          <label className="bot__switch">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => {
                setDraft({ ...draft, enabled: event.target.checked });
              }}
            />
            <span>Enabled</span>
          </label>

          <div className="bot__actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save reply'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setDraft(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {rules === null ? (
        <p className="field__hint">Loading…</p>
      ) : rules.length === 0 ? (
        <EmptyState title="No automatic replies yet">An automatic reply answers a phrase rather than a command — “where's the discord?”, for example.</EmptyState>
      ) : (
        <ul className="bot__list">
          {rules.map((rule) => (
            <li className="bot__item" key={rule.id}>
              <div className="bot__item-head">
                <span className="bot__item-name">{rule.name}</span>
                <StatusPill
                  tone={rule.enabled ? 'ok' : 'idle'}
                  label={rule.enabled ? 'Enabled' : 'Disabled'}
                />
              </div>
              <span className="field__hint">
                When a message {KINDS.find((kind) => kind.value === rule.kind)?.label ?? rule.kind}{' '}
                “{rule.triggerValue}”
              </span>
              <span className="bot__item-detail">{rule.body}</span>

              {mayManage && (
                <div className="bot__actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setDraft({
                        id: rule.id,
                        name: rule.name,
                        kind: rule.kind,
                        triggerValue: rule.triggerValue,
                        body: rule.body,
                        cooldownSeconds: rule.cooldownSeconds,
                        enabled: rule.enabled,
                      });
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="field__hint">
        Replies are sent on the platform the message came from. The bot never answers its own
        messages, and {String(settings.platforms.length)} platform
        {settings.platforms.length === 1 ? ' is' : 's are'} connected.
      </p>

      <VariableHelp variables={variables} />
    </div>
  );
}
